






























import {mapActions, mapGetters} from 'vuex';
import {tagRecognitionRegex} from '@/store/constants';
import {validationMixin} from 'vuelidate';
import {maxLength, required} from 'vuelidate/lib/validators';
import {emptyAdvert} from '@/store/modules/Advert/state';
import vue from 'vue';
import {Advert} from '#/Advert';
import {TranslateResult} from 'vue-i18n';

interface ComponentData {
  advertLocal: Advert;
  dialog: { display: boolean };
  publish: boolean;
}

export default vue.extend({
  name: 'AdvertCreation',

  components: {
    'category-selection': () => import('@/components/advert/creation/Category.vue'),
    'participants-limit': () => import('@/components/advert/creation/ParticipantsLimit.vue'),
    'period-selection': () => import('@/components/advert/creation/Period.vue'),
    'type-selection': () => import('@/components/advert/creation/Type.vue'),
    'update-content': () => import('@/components/advert/creation/Content.vue'),
  },

  mixins: [validationMixin],

  props: ['advertId'],

  data: (): ComponentData => ({
    advertLocal: {
      ...emptyAdvert(),
    },
    dialog: {display: false},
    publish: false,
  }),

  computed: {
    ...mapGetters([
      'advert',
      'categoryFromId',
    ]),
    /**
     * Retourne l'état de l'annonce
     *
     * @return {string}
     */
    advertState(): 'PUBLISHED' | 'ARCHIVED' | 'DRAFT' {
      return this.advert.archivedAt !== null
          ? this.advert.isPublished ? 'PUBLISHED' : 'ARCHIVED'
          : 'DRAFT';
    },
    /**
     * Retourne le label pour le bouton d'enregistrement en fonction de :
     * - l'état de l'annonce
     * - si l'utilisateur a coché le fait de publier ou non l'annonce
     *
     * @return {string}
     */
    buttonValidText(): TranslateResult {
      switch (this.advertState) {
        case 'DRAFT':
          return this.advertLocal.isPublished
              ? this.$t('advert.creation.validationButtons.draft.publish')
              : this.$t('advert.creation.validationButtons.draft.save');

        case 'ARCHIVED':
          return this.advertLocal.isPublished
              ? this.$t('advert.creation.validationButtons.advertArchived.republish')
              : this.$t('advert.creation.validationButtons.advertArchived.save');

        case 'PUBLISHED':
          return this.advertLocal.isPublished
              ? this.$t('advert.creation.validationButtons.advertPublished.save')
              : this.$t('advert.creation.validationButtons.advertPublished.archive');

        default:
          return '';
      }
    },
    errorCategory(): TranslateResult[] {
      const errors: TranslateResult[] = [];
      if (!this.$v.advertLocal.categories?.$dirty) return errors;
      if (!this.$v.advertLocal.categories?.required) {
        errors.push(this.$t('advert.creation.errors.category.required'));
      }
      return errors;
    },
    errorContent(): TranslateResult[] {
      const errors: TranslateResult[] = [];
      if (!this.$v.advertLocal.content?.$dirty) return errors;
      if (!this.$v.advertLocal.content?.required) {
        errors.push(this.$t('advert.creation.errors.content.required'));
      }
      return errors;
    },
    errorTitle(): TranslateResult[] {
      const errors: TranslateResult[] = [];
      if (!this.$v.advertLocal.title?.$dirty) return errors;
      if (!this.$v.advertLocal.title.required) {
        errors.push(this.$t('advert.creation.errors.title.required'));
      }
      if (!this.$v.advertLocal.title?.maxLength) {
        errors.push(this.$t('advert.creation.errors.title.required'));
      }
      return errors;
    },
    errorType(): TranslateResult[] {
      const errors: TranslateResult[] = [];
      if (!this.$v.advertLocal.type?.$dirty) return errors;
      if (!this.$v.advertLocal.type?.required) {
        errors.push(this.$t('advert.creation.errors.type.required'));
      }
      return errors;
    },
    isUpdate(): boolean {
      return this.$route.name === 'AdvertUpdate';
    },
    isArchive(): boolean {
      return !this.advertLocal.isPublished && this.advert.isPublished;
    },
    categoryFilter(): boolean {
      return Object.keys(this.$route.query)
                   .includes('category') &&
          this.categoryFromId(parseInt(this.$route.query?.category as string));
    },
  },

  validations: {
    advertLocal: {
      title: {
        required,
        maxLength: maxLength(255),
      },
      content: {
        required,
      },
      type: {
        required,
      },
      categories: {
        required,
        $each: {
          required,
        },
      },
    },
  },

  created() {
    this.loadCategories()
        .then(() => {
          if (this.isUpdate) {
            this.updatePageTitle(this.$t('advert.update.title'));
            this.loadAdvert(this.advertId)
                .then(() => {
                  this.advertLocal = {...this.advert};
                });
          } else {
            this.updatePageTitle(this.$t('advert.creation.titles.main'));
            this.resetAdvert()
                .then(() => {
                  this.advertLocal = {
                    ...this.advert,
                    categories: this.categoryFilter ? [this.categoryFilter] : [],
                  };
                });
          }
        });

    // TODO: sécurité vérification des informations avant envoie
  },

  methods: {
    ...mapActions([
      'archiveAdvert',
      'resetAdvert',
      'loadCategories',
      'loadAdvert',
      'saveAdvert',
      'updateAdvert',
      'updatePageTitle',
    ]),
    onSaveAdvert(): void {
      const newAdvert = this.computeAdvertProperties(this.advertLocal);

      this.$v.$touch();

      if (!this.$v.$invalid) {
        if (this.advertLocal.isPublished) {
          if (!this.$v.$invalid) {
            this.isArchive
                ? this.archiveAdvert(newAdvert)
                : this.isUpdate
                    ? this.updateAdvert(newAdvert)
                    : this.saveAdvert(newAdvert);
          }
        } else {
          this.isArchive
              ? this.archiveAdvert(newAdvert)
              : this.isUpdate
                  ? this.updateAdvert(newAdvert)
                  : this.saveAdvert(newAdvert);
        }
      }
    },
    /**
     * Retire les champs d'annonce qui ne sont jamais mis à jour par l'utilisateur
     * et formate les autres champs
     *
     * @param newAdvert
     */
    computeAdvertProperties(
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        {author, archivedAt, createdAt, updatedAt, period, title, categories, type, ...advert}: Advert,
    ) {
      // get uniq tag list from advert content and clean preceding chars(# included)
      let tags = advert.content.match(tagRecognitionRegex);
      tags = tags ? [...new Set(tags.map(tag => tag.slice(tag.indexOf('#') + 1)))] : [];

      // get Date objects from dates selection or null
      const [startsAt, endsAt] = period.datesSelected
                                       .map(d => new Date(d))
                                       .concat(Array(2 - period.datesSelected.length).fill(null));

      return {
        ...advert,
        tags,
        type: type.raw,
        title: title === '' ? this.$t('advert.creation.defaultTitle') : title,
        categories: categories.map(cat => cat.id),
        startsAt,
        endsAt,
        availability: period.isDate ? null : period.periodSelected,
      };
    },
  },
});
